import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { setSessionFacility } from "@/lib/api";
import { localeFor } from "@/lib/money";

// Money always in the gym's own currency: never one symbol for every gym.
// Each row uses its own gym's digit grouping (India 1,80,000; elsewhere 180,000).
const nf = (code) => new Intl.NumberFormat(localeFor(code), { maximumFractionDigits: 0 });
const nf2 = (code) => new Intl.NumberFormat(localeFor(code), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const sym = (row) => (row?.currency_symbol && String(row.currency_symbol).trim()) ? row.currency_symbol : `${row?.currency_code || ""} `;
export const money = (row, n, exact = false) => {
  const v = Number(n) || 0;
  const s = (exact ? nf2 : nf)(row?.currency_code).format(Math.abs(v));
  return `${v < 0 ? "−" : ""}${sym(row)}${s}`;
};
export const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Math.round(v * 100)}%`);

export const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

export function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function CsvButton({ onClick, disabled, testid }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick} disabled={disabled}
      className="border-[#262626] bg-transparent text-zinc-300 hover:bg-[#1a1a1a] h-8" data-testid={testid}>
      <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
    </Button>
  );
}

/** Open a record in its gym: switch the session to that gym, then go to the page (searching for the member). */
export async function openInGym(link) {
  if (!link) return;
  await setSessionFacility(link.facility_id);
  window.location.href = `${link.path}${link.q ? `?q=${encodeURIComponent(link.q)}` : ""}`;
}

/** Click-to-sort table state. `cols` = {key: (row) => value}. */
export function useSort(rows, cols, initial, initialDir = "desc") {
  const [key, setKey] = useState(initial);
  const [dir, setDir] = useState(initialDir);
  const sorted = useMemo(() => {
    const get = cols[key] || (() => 0);
    return [...(rows || [])].sort((a, b) => {
      const x = get(a), y = get(b);
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""));
      return dir === "asc" ? c : -c;
    });
  }, [rows, cols, key, dir]);
  const toggle = (k) => {
    if (k === key) setDir(dir === "asc" ? "desc" : "asc");
    else { setKey(k); setDir("desc"); }
  };
  return { sorted, key, dir, toggle };
}

export function SortTh({ sort, k, children, right }) {
  const on = sort.key === k;
  return (
    <th className={`px-3 py-2 font-medium cursor-pointer select-none hover:text-zinc-300 ${right ? "text-right" : ""}`}
      onClick={() => sort.toggle(k)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {on && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

export const TH = "text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-[#1f1f1f]";
export const TR = "border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]";

export function Empty({ children }) {
  return <div className="px-4 py-10 text-center text-sm text-zinc-500">{children}</div>;
}

export function Stat({ label, value, sub, tone }) {
  const color = tone === "good" ? "text-emerald-300" : tone === "bad" ? "text-rose-300" : "text-zinc-100";
  return (
    <div className="card-surface px-4 py-3 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
